/*
 * AugLoopHelper.ts
 */
import { __awaiter } from "tslib";
import { SchemaObject } from "@augloop/types-core";
import { getDynamicSetting } from "../Configuration/DynamicConfiguration";
import { AUGLOOP_INIT_CHECK_RETRY_TIMES } from "./TargetedMessagingConstants";
function sleep(milliseconds) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
}
function getObjectProperty(targetObject, propertyName) {
  const propertyValue = targetObject && targetObject[propertyName];
  return propertyValue || Object.getOwnPropertyDescriptor(targetObject, propertyName) ? propertyValue : undefined;
}
// listen to property change on targetObject and return the value when it is set
function getDelayDefinedObjectValue(targetObject, propertyName) {
  if (!targetObject) {
    return Promise.resolve(null);
  }
  const currentPropertyValue = getObjectProperty(targetObject, propertyName);
  if (currentPropertyValue) {
    return Promise.resolve(currentPropertyValue);
  }
  return new Promise((resolve, reject) => {
    let propertyValue;
    Object.defineProperty(targetObject, propertyName, {
      configurable: true,
      enumerable: true,
      get: () => propertyValue,
      set: newValue => {
        propertyValue = newValue;
        resolve(propertyValue);
      }
    });
  });
}
function getDelayDefinedValueWithWait(targetObject, propertyName, retryTimes) {
  let delay = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 100;
  return __awaiter(this, void 0, void 0, function* () {
    const definedProperty = getObjectProperty(targetObject, propertyName);
    if (definedProperty || retryTimes === 0) {
      return definedProperty;
    }
    yield sleep(delay);
    return getDelayDefinedValueWithWait(targetObject, propertyName, retryTimes - 1, delay);
  });
}
// Invoke AL calls and get the data
function getAnnotationData(augLoopSession, annotationName) {
  return new Promise((resolve, reject) => {
    augLoopSession.activateAnnotation(annotationName, {
      callback: operation => {
        try {
          const operationItems = operation && operation.items ? operation.items : [];
          const schemaObjects = operationItems.filter(item => annotationName === SchemaObject.getTypeNameFor(item.body));
          resolve(schemaObjects && schemaObjects[0] && schemaObjects[0].body);
        } catch (error) {
          resolve({
            error: error
          });
        }
      }
    });
  });
}
// Get the Augloop global object - window.augLoop
function getGlobalAugLoopRuntime() {
  let loadDelay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 100;
  return __awaiter(this, void 0, void 0, function* () {
    // try to get the augloop global runtime window.augLoop
    let augLoopRuntime = getObjectProperty(window, "augLoop");
    if (!augLoopRuntime) {
      const delayLoadTime = getDynamicSetting("TulipsAugLoopPackageDelayLoad" /* tulipsAugLoopPackageDelayLoad */, -1);
      if (delayLoadTime > 0) {
        // wait for augloop to load after sometime
        yield sleep(delayLoadTime);
        augLoopRuntime = getObjectProperty(window, "augLoop");
      } else {
        const retryTimes = getDynamicSetting("TulipsAugLoopLoadRetryTimes" /* tulipsAugLoopLoadRetryTimes */, AUGLOOP_INIT_CHECK_RETRY_TIMES);
        augLoopRuntime = yield getDelayDefinedValueWithWait(window, "augLoop", retryTimes, loadDelay);
        if (!augLoopRuntime) {
          // Wait for augloop to mount on window
          augLoopRuntime = yield getDelayDefinedObjectValue(window, "augLoop");
        }
        if (loadDelay > 0) {
          yield sleep(loadDelay);
        }
      }
    }
    return augLoopRuntime;
  });
}
export function getAugLoopAnnotationData(annotationName, getRuntimeSession) {
  let loadDelay = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 100;
  return __awaiter(this, void 0, void 0, function* () {
    let augLoopSession = getRuntimeSession && (yield getRuntimeSession());
    if (!augLoopSession) {
      const augLoopRuntime = yield getGlobalAugLoopRuntime(loadDelay);
      if (!augLoopRuntime) {
        throw new Error("Unable to get AL runtime");
      }
      const augLoopSessionPromise = window.augLoop.ALFactoryGlobal.getAugLoopRuntimeManager().getSession();
      if (!augLoopSessionPromise) {
        throw new Error("Unable to get AL session promise");
      }
      augLoopSession = yield augLoopSessionPromise;
      if (!augLoopSession) {
        throw new Error("Unable to get AL session");
      }
    }
    return getAnnotationData(augLoopSession, annotationName);
  });
}